import { useDispatch, useSelector } from 'react-redux';
import { useLazyGetMsiQuery } from './msi.api';
import {
  selectMsi,
  setMSI,
  restore,
  setMsiSelected as msiSelected,
  removeSelected,
} from './msi.slice';
import max from 'lodash/max';
import { isToday, isNext } from '@/locales/dates';

const parsePromoData = (promo) => ({
  startAt: promo.start_at,
  endAt: promo.end_at,
  minimumPurchase: promo.minimum_purchase || 0,
  isToday: isToday(promo.start_at, promo.end_at),
  isNext: isNext(promo.start_at),
  bines: {},
  banks: {},
  binesByBanks: {},
});

export default function useMsi() {
  const dispatch = useDispatch();
  const msi = useSelector(selectMsi);
  const [trigger] = useLazyGetMsiQuery();

  const removeBin = () => {
    dispatch(removeSelected());
  };

  const setMsiSelected = (bin, bank, payment_plan) => {
    dispatch(
      msiSelected({
        bin,
        bank,
        payment_plan,
      }),
    );
  };

  const load = async (eventId) => {
    try {
      if (eventId) {
        const { data } = await trigger(eventId, false);
        if (data.detail?.banks) {
          dispatch(setMSI({ ...data, ...parseData(data) }));
        } else {
          dispatch(setMSI(null));
        }
      }
    } catch {
      dispatch(setMSI(null));
    }
  };

  const parseData = (d) => {
    let currentMsi = null;
    let currentPreSale = null;
    if (d.detail.banks) {
      d.detail.banks.map((bank) => {
        bank.bines.map((bin) => {
          const preSalesOptions = bin.promotions.filter(
            (promo) => promo.type === 'pre-sale',
          );
          const msiOptions = bin.promotions.filter(
            (promo) => promo.type === 'msi',
          );

          if (preSalesOptions.length > 0) {
            if (!currentPreSale) {
              currentPreSale = parsePromoData(preSalesOptions[0]);
            }
            currentPreSale = {
              ...currentPreSale,
              options: preSalesOptions.map((promo) => promo.option),
            };
            currentPreSale.bines = {
              ...currentPreSale.bines,
              [`${bin.bin}`]: {
                bank: bank.bank_name,
                card: bin.card_name,
                bin: bin.bin,
              },
            };
            if (!currentPreSale.banks[bank.bank_name]) {
              currentPreSale.banks[bank.bank_name] = [bin.card_name];
            } else {
              currentPreSale.banks[bank.bank_name] = [
                ...currentPreSale.banks[bank.bank_name],
                bin.card_name,
              ];
            }

            if (!currentPreSale.binesByBanks[bank.bank_name]) {
              currentPreSale.binesByBanks[bank.bank_name] = [
                { card_name: bin.card_name, bin: bin.bin },
              ];
            } else {
              currentPreSale.binesByBanks[bank.bank_name] = [
                ...currentPreSale.binesByBanks[bank.bank_name],
                { card_name: bin.card_name, bin: bin.bin },
                ,
              ];
            }

            currentPreSale.minimumPurchase =
              max(
                bin.promotions
                  .filter((promo) => promo.type === 'pre-sale')
                  .map((promo) => promo.minimum_purchase),
              ) || 0;
          }

          if (msiOptions.length > 0) {
            if (!currentMsi) {
              currentMsi = parsePromoData(msiOptions[0]);
            }
            currentMsi = {
              ...currentMsi,
              options: msiOptions.map((promo) => promo.option),
            };
            currentMsi.bines = {
              ...currentMsi.bines,
              [`${bin.bin}`]: {
                bank: bank.bank_name,
                card: bin.card_name,
                bin: bin.bin,
              },
            };
            if (!currentMsi.banks[bank.bank_name]) {
              currentMsi.banks[bank.bank_name] = [bin.card_name];
            } else {
              currentMsi.banks[bank.bank_name] = [
                ...currentMsi.banks[bank.bank_name],
                bin.card_name,
              ];
            }
            if (!currentMsi.binesByBanks[bank.bank_name]) {
              currentMsi.binesByBanks[bank.bank_name] = [
                { card_name: bin.card_name, bin: bin.bin },
              ];
            } else {
              currentMsi.binesByBanks[bank.bank_name] = [
                ...currentMsi.binesByBanks[bank.bank_name],
                { card_name: bin.card_name, bin: bin.bin },
              ];
            }
            currentMsi.minimumPurchase =
              max(
                bin.promotions
                  .filter((promo) => promo.type === 'msi')
                  .map((promo) => promo.minimum_purchase),
              ) || 0;
          }
        });
      });
    } else {
      null;
    }

    return {
      msi: currentMsi,
      preSale: currentPreSale,
    };
  };

  const isPreSaleBinValid = (cardNumber) => {
    if (msi && msi.preSale) {
      const resp = Object.keys(msi.preSale.bines).find((bin) =>
        cardNumber.startsWith(bin),
      );
      if (resp) {
        return msi.preSale.bines[resp];
      }
    }

    return false;
  };

  const isMsiBinValid = (cardNumber) => {
    if (msi && msi.msi) {
      const resp = Object.keys(msi.msi.bines).find((bin) =>
        cardNumber.startsWith(bin),
      );
      if (resp) {
        return msi.msi.bines[resp];
      }
    }

    return false;
  };

  return {
    load,
    setMsiSelected,
    removeBin,
    msi,
    restore,
    isPreSaleBinValid,
    isMsiBinValid,
  };
}
